import { getTranslation } from '@cibli/shared';
import i18n from 'i18next';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useFastpassLanguage } from '@/features/language/hooks/useFastpassLanguage';
import { useBooth } from '@/features/settings/hooks/useBooth';
import api, { setApiLanguage } from '@/services/api';
import { translateCV } from '@/services/interview';

interface LanguageContextState {
  setLanguage: (language: string, persist?: boolean) => Promise<void>;
  writeFastpassLanguage: (language: string) => void;
  clearFastpassLanguage: () => void;
  /** Translate the current CV to a target language. No-op if no CV is in scope. */
  translateCvTo: (targetLanguage: string, cvId: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextState | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { setCurrentLocaleCv } = useCVTemplateContext();
  const { platformType, isWeb, webRegionConfig, booth, loading, error } = useBooth();
  // BO-configured base language of the device (« Langue principale de la
  // cabine » — affichée à l'écran), authoritative on boot for kiosks.
  const boothDefaultLocale = booth?.defaultLocaleId;
  const navigate = useNavigate();
  const inFlightRef = useRef<Map<string, Promise<void>>>(new Map());

  const ensureTranslationLoaded = useCallback(async (language: string) => {
    if (i18n.hasResourceBundle(language, 'translation')) return;

    const existing = inFlightRef.current.get(language);
    if (existing) {
      await existing;
      return;
    }

    const promise = getTranslation(language, {
      api,
      useRemote: import.meta.env.VITE_USE_WEBLATE === 'true',
    })
      .then(data => {
        if (data.translations) {
          i18n.addResourceBundle(language, 'translation', data.translations, true, true);
        }
      })
      .finally(() => {
        inFlightRef.current.delete(language);
      });

    inFlightRef.current.set(language, promise);
    await promise;
  }, []);

  const handleI18nLoadAndSetLanguage = useCallback(
    async (newLanguage: string) => {
      await ensureTranslationLoaded(newLanguage);
      await i18n.changeLanguage(newLanguage);
    },
    [ensureTranslationLoaded],
  );

  const setLanguage = useCallback(
    async (newLanguage: string) => {
      await handleI18nLoadAndSetLanguage(newLanguage);
      setApiLanguage(newLanguage);
      setCurrentLocaleCv(newLanguage);
      document.documentElement.lang = newLanguage;
      try {
        localStorage.setItem('i18nextLng', newLanguage);
      } catch {
        // Ignore localStorage errors (private browsing or restricted context)
      }
    },
    [handleI18nLoadAndSetLanguage, setCurrentLocaleCv],
  );

  const translateCvTo = useCallback(
    async (targetLanguage: string, cvId: string) => {
      try {
        await ensureTranslationLoaded(targetLanguage);
        const { fullCvTranslated } = await translateCV(cvId, { targetLanguage });
        if (fullCvTranslated) {
          setCurrentLocaleCv(targetLanguage);
          toast.success('CV traduit avec succès');
        }
      } catch (error) {
        console.error('Failed to translate CV:', error);
        toast.error('Erreur lors de la traduction du CV');
      }
    },
    [ensureTranslationLoaded, setCurrentLocaleCv],
  );

  /** Used after Android language-change reload: restore language and skip to onboarding. */
  const handleFastpassLanguage = useCallback(
    (language: string) => {
      void setLanguage(language);
      navigate('/onboarding', { replace: true });
    },
    [setLanguage, navigate],
  );

  const { writeFastpassLanguage, clearFastpassLanguage } = useFastpassLanguage(
    platformType,
    handleFastpassLanguage,
  );

  // Sync lang on mount & resolve initial language. Spec flow 2026-10-22 :
  // - l'écran d'accueil démarre dans la langue de la LOCALE DE BASE
  //   (cabine ou région web) — une fois par montage, puis le choix du
  //   candidat (sélecteur) reste respecté jusqu'au prochain rechargement ;
  // - les replis (langue sauvegardée / navigateur) ne s'exécutent QU'UNE
  //   fois et jamais quand la config est en cours ou en erreur : un échec
  //   du booth ne doit plus faire basculer l'app sur une langue périmée
  //   (ex. es-ES alors que la base est es-MX).
  const appliedBaseRef = useRef<string | null>(null);
  const bootFallbackRanRef = useRef(false);
  useEffect(() => {
    document.documentElement.lang = i18n.language;

    // 1. Configured base locale wins on boot — booth devices first, then the
    //    web region default.
    const baseLocale =
      (!isWeb && boothDefaultLocale) ||
      (isWeb ? webRegionConfig?.defaultLocale?.codeLong : undefined);
    if (baseLocale && baseLocale !== appliedBaseRef.current) {
      appliedBaseRef.current = baseLocale;
      void setLanguage(baseLocale);
      return;
    }

    if (appliedBaseRef.current || bootFallbackRanRef.current) {
      return;
    }

    // Config still resolving… or failed: never guess, never flip.
    if (loading || error || (!booth && !webRegionConfig)) {
      return;
    }

    // 2. If candidate explicitly chose a language before, respect it
    try {
      const savedLang = localStorage.getItem('i18nextLng');
      if (savedLang && savedLang !== i18n.language) {
        bootFallbackRanRef.current = true;
        void setLanguage(savedLang);
        return;
      }
    } catch {
      // Ignore
    }

    // 3. Fallback: Auto-detect from browser locale if Spanish (Mexico vs Spain), German, etc.
    bootFallbackRanRef.current = true;
    if (typeof navigator !== 'undefined' && navigator.language) {
      const navLang = navigator.language.toLowerCase();
      if (navLang.startsWith('es-mx') && i18n.language !== 'es-MX') {
        void setLanguage('es-MX');
      } else if (navLang.startsWith('es') && i18n.language !== 'es-ES' && i18n.language !== 'es') {
        void setLanguage('es-ES');
      }
    }
  }, [setLanguage, isWeb, webRegionConfig, booth, boothDefaultLocale, loading, error]);

  const value: LanguageContextState = {
    setLanguage,
    writeFastpassLanguage,
    clearFastpassLanguage,
    translateCvTo,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguageContext(): LanguageContextState {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}
